import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../store";
import AccommodationSystems from "../components/optional-systems/AccommodationSystems";
import BridgeSystems from "../components/optional-systems/BridgeSystems";
import CargoSystems from "../components/optional-systems/CargoSystems";
import DroneSystems from "../components/optional-systems/DroneSystems";
import DriveSystems from "../components/optional-systems/DriveSystems";
import ExternalSystems from "../components/optional-systems/ExternalSystems";
import FuelSystems from "../components/optional-systems/FuelSystems";
import InternalSystems from "../components/optional-systems/InternalSystems";
import ModularHullSystems from "../components/optional-systems/ModularHullSystems";
import PowerSystems from "../components/optional-systems/PowerSystems";
import SensorSystems from "../components/optional-systems/SensorSystems";
import StructureSystems from "../components/optional-systems/StructureSystems";
import ErrorBoundary from "../components/optional-systems/ErrorBoundary";
import { OPTIONAL_SYSTEM_TYPES } from "../../../constants/optionalSystems";
import {
  StructureSystem,
  ModularHullSystem,
  PowerSystem,
  DriveSystem,
  FuelSystem,
  AccommodationSystem,
  BridgeSystem,
  CargoSystem,
  ExternalSystem,
  InternalSystem,
} from "../../../types/optionalSystems";
import { selectors as optionalSystemsSelectors } from "../../../store/selectors/optionalSystemsSelectors";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import { useAppSelector as useBreakawaySelector } from "../../../store/hooks";

import {
  isStructureSystemGuard,
  isModularHullSystemGuard,
  isPowerSystemGuard,
  isDriveSystemGuard,
  isFuelSystemGuard,
  isAccommodationSystemGuard,
  isBridgeSystemGuard,
  isCargoSystemGuard,
  isDroneSystemGuard,
  isSensorSystemGuard,
  isExternalSystemGuard,
  isInternalSystemGuard,
} from "../components/optional-systems/typeGuards";
import { formatCurrency } from "../../../utils/formatting";
import {
  removeStructureSystem,
  removeModularHullSystem,
  removePowerSystem,
  removeDriveSystem,
  removeFuelSystem,
  removeAccommodationSystem,
  removeBridgeSystem,
  removeCargoSystem,
  removeDroneSystem,
  removeSensorSystem,
  removeExternalSystem,
  removeInternalSystem,
  removeCommonAreaSystem, // <-- add this import
} from "../../../store";
import CommonAreasSystems from "../components/optional-systems/CommonAreasSystems";
import type { OptionalSystem } from "../../../types/optionalSystems";
import {
  groupSystems,
  removeGroupedSystem,
  isGroupedSystem,
} from "../../../utils/helpers/systemHelpers";



interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`optional-systems-tabpanel-${index}`}
      aria-labelledby={`optional-systems-tab-${index}`}
      {...other}
    >
      {value === index && <div className="p-6">{children}</div>}
    </div>
  );
}

const OptionalSystems: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [systemErrors, setSystemErrors] = useState<Record<string, string>>({});
  const [systemSectionAssignments, setSystemSectionAssignments] = useState<Record<string, string>>({});
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);
  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);
  const hull = useAppSelector((state) => state.shipBuilder.hull);
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const breakawayHull = useBreakawaySelector((state) => state.breakawayHull);
  const isStreamlined = useAppSelector(
    (state) => state.shipBuilder.hull?.configuration?.streamlined === "yes",
  );
  const isDispersedStructure = useAppSelector(
    (state) =>
      state.shipBuilder.hull?.configuration?.type === "dispersedStructure",
  );
  const installedSystems = useAppSelector(
    optionalSystemsSelectors.selectAllOptionalSystems,
  );
  const optionalSystems = useAppSelector((state) => state.optionalSystems);
  const totalCost = useAppSelector(
    optionalSystemsSelectors.selectTotalOptionalSystemsCost,
  );
  const totalTonnage = useAppSelector(
    optionalSystemsSelectors.selectTotalOptionalSystemsTonnage,
  );
  const totalPower = useAppSelector(
    optionalSystemsSelectors.selectTotalOptionalSystemsPower,
  );
  const shipState = useAppSelector((state) => state.shipBuilder);
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const dispatch = useAppDispatch();
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const sizeOverrideActive = useAppSelector(selectors.selectSizeOverrideActive);





  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleSystemError = (
    error: Error,
    errorInfo: React.ErrorInfo,
    systemType: string,
  ) => {
    console.error(`Error in ${systemType} system:`, error, errorInfo);
    setSystemErrors((prev) => ({
      ...prev,
      [systemType]: `Failed to load ${systemType} systems. Please try again.`,
    }));
  };

  // Helper function to get available systems by type from constants
  const getStructureSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "structure",
      ) as StructureSystem[];
    } catch (error) {
      console.error("Error loading structure systems:", error);
      return [];
    }
  };

  const getModularHullSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "modularHull",
      ) as ModularHullSystem[];
    } catch (error) {
      console.error("Error loading modular hull systems:", error);
      return [];
    }
  };

  const getPowerSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "power",
      ) as PowerSystem[];
    } catch (error) {
      console.error("Error loading power systems:", error);
      return [];
    }
  };

  const getDriveSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "drive",
      ) as DriveSystem[];
    } catch (error) {
      console.error("Error loading drive systems:", error);
      return [];
    }
  };

  const getFuelSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "fuel",
      ) as FuelSystem[];
    } catch (error) {
      console.error("Error loading fuel systems:", error);
      return [];
    }
  };

  const getAccommodationSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "accommodation",
      ) as AccommodationSystem[];
    } catch (error) {
      console.error("Error loading accommodation systems:", error);
      return [];
    }
  };

  const getBridgeSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "bridge",
      ) as BridgeSystem[];
    } catch (error) {
      console.error("Error loading bridge systems:", error);
      return [];
    }
  };

  const getCargoSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "cargo",
      ) as CargoSystem[];
    } catch (error) {
      console.error("Error loading cargo systems:", error);
      return [];
    }
  };

  const getExternalSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "external",
      ) as ExternalSystem[];
    } catch (error) {
      console.error("Error loading external systems:", error);
      return [];
    }
  };

  const getInternalSystems = () => {
    try {
      return OPTIONAL_SYSTEM_TYPES.filter(
        (system) => system.type === "internal",
      ) as InternalSystem[];
    } catch (error) {
      console.error("Error loading internal systems:", error);
      return [];
    }
  };

  // Validate ship configuration for optional systems
  const getShipConfigurationWarnings = () => {
    const warnings: string[] = [];

    if (!shipSize || shipSize <= 0) {
      warnings.push(
        "Ship size is not configured. Please complete the Hull step first.",
      );
    }

    if (!techLevel || techLevel <= 0) {
      warnings.push(
        "Tech level is not configured. Please complete the Hull step first.",
      );
    }

    if (!powerPlant) {
      warnings.push(
        "Power plant is not configured. Please complete the Power Plant step first.",
      );
    }

    if (!hull) {
      warnings.push(
        "Hull configuration is missing. Please complete the Hull step first.",
      );
    }

    return warnings;
  };

  const shipWarnings = getShipConfigurationWarnings();

  const systemCategories = [
    {
      label: "Structure",
      value: "structure",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Structure")
          }
        >
          <StructureSystems
            availableSystems={getStructureSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Modular Hull",
      value: "modularHull",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Modular Hull")
          }
        >
          <ModularHullSystems
            availableSystems={getModularHullSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Power",
      value: "power",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Power")
          }
        >
          <PowerSystems
            availableSystems={getPowerSystems()}
            powerPlantTonnage={powerPlant?.weight || 0}
            powerPlantCost={powerPlant?.cost || 0}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Drives",
      value: "drives",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Drives")
          }
        >
          <DriveSystems
            availableSystems={getDriveSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Fuel",
      value: "fuel",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Fuel")
          }
        >
          <FuelSystems
            availableSystems={getFuelSystems()}
            shipTonnage={shipSize}
            isStreamlined={Boolean(hull?.configuration?.streamlined)}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Accommodations",
      value: "accommodations",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Accommodations")
          }
        >
          <AccommodationSystems
            availableSystems={getAccommodationSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Bridge",
      value: "bridge",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Bridge")
          }
        >
          <BridgeSystems
            availableSystems={getBridgeSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
            sizeOverrideActive={sizeOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Cargo",
      value: "cargo",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Cargo")
          }
        >
          <CargoSystems
            availableSystems={getCargoSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            isStreamlined={isStreamlined}
            isDispersedStructure={isDispersedStructure}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Drones",
      value: "drones",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Drones")
          }
        >
          <DroneSystems shipTonnage={shipSize} techLevel={techLevel} tlOverrideActive={tlOverrideActive} />
        </ErrorBoundary>
      ),
    },
    {
      label: "Sensors",
      value: "sensors",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Sensors")
          }
        >
          <SensorSystems
            availableSystems={getInternalSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "External",
      value: "external",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "External")
          }
        >
          <ExternalSystems
            availableSystems={getExternalSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            isStreamlined={Boolean(hull?.configuration?.streamlined)}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Internal",
      value: "internal",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Internal")
          }
        >
          <InternalSystems
            availableSystems={getInternalSystems()}
            shipTonnage={shipSize}
            techLevel={techLevel}
            tlOverrideActive={tlOverrideActive}
          />
        </ErrorBoundary>
      ),
    },
    {
      label: "Common Areas",
      value: "commonAreas",
      component: (
        <ErrorBoundary
          onError={(error, errorInfo) =>
            handleSystemError(error, errorInfo, "Common Areas")
          }
        >
          <CommonAreasSystems />
        </ErrorBoundary>
      ),
    },
  ];

  // Helper to get the correct remove action for a system
  const handleRemoveSystem = (system: OptionalSystem) => {
    switch (system.type) {
      case "structure":
        dispatch(removeStructureSystem(system.id));
        break;
      case "modularHull":
        dispatch(removeModularHullSystem(system.id));
        break;
      case "power":
        dispatch(removePowerSystem(system.id));
        break;
      case "drive":
        dispatch(removeDriveSystem(system.id));
        break;
      case "fuel":
        dispatch(removeFuelSystem(system.id));
        break;
      case "accommodation":
        dispatch(removeAccommodationSystem(system.id));
        break;
      case "bridge":
        dispatch(removeBridgeSystem(system.id));
        break;
      case "cargo":
        dispatch(removeCargoSystem(system.id));
        break;
      case "drone":
        dispatch(removeDroneSystem(system.id));
        break;
      case "sensor":
        dispatch(removeSensorSystem(system.id));
        break;
      case "external":
        dispatch(removeExternalSystem(system.id));
        break;
      case "internal":
        dispatch(removeInternalSystem(system.id));
        break;
      case "commonArea":
        dispatch(removeCommonAreaSystem(system.id));
        break;
      default:
        break;
    }
  };

  // Handler for Edit button
  // const handleEditSystem = (system: any) => { /* unused */ };

  // Handler for confirming edit (for Armoured Bulkhead)
  // const handleConfirmEdit = (protectedIds: string[], protectedTonnage: number) => { /* unused */ };

  // Handler to cancel editing
  // const handleCancelEdit = () => { /* unused */ };

  // Section assignment handlers for breakaway hulls
  const handleSectionAssignmentToggle = (systemId: string, isAssigned: boolean) => {
    if (isAssigned) {
      // If assigning, set to first available section
      const firstSection = breakawayHull?.sections?.[0];
      if (firstSection) {
        setSystemSectionAssignments(prev => ({
          ...prev,
          [systemId]: firstSection.id
        }));
        // Update the optional system in Redux with sectionId
        updateOptionalSystemSectionId(systemId, firstSection.id, firstSection.name);
      }
    } else {
      // If unassigning, remove the assignment
      setSystemSectionAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[systemId];
        return newAssignments;
      });
      // Remove sectionId from the optional system in Redux
      updateOptionalSystemSectionId(systemId, undefined, undefined);
    }
  };

  const handleSectionChange = (systemId: string, sectionId: string) => {
    setSystemSectionAssignments(prev => ({
      ...prev,
      [systemId]: sectionId
    }));
    // Update the optional system in Redux with new sectionId
    const section = breakawayHull?.sections?.find(s => s.id === sectionId);
    if (section) {
      updateOptionalSystemSectionId(systemId, sectionId, section.name);
    }
  };

  // Helper function to update optional system section assignment in Redux
  const updateOptionalSystemSectionId = (systemId: string, sectionId: string | undefined, sectionName: string | undefined) => {
    // Find the system in all optional system arrays and update it
    const allSystems = [
      ...(Array.isArray(optionalSystems.structure) ? optionalSystems.structure : []),
      ...(Array.isArray(optionalSystems.modularHull) ? optionalSystems.modularHull : []),
      ...(Array.isArray(optionalSystems.power) ? optionalSystems.power : []),
      ...(Array.isArray(optionalSystems.drives) ? optionalSystems.drives : []),
      ...(Array.isArray(optionalSystems.fuel) ? optionalSystems.fuel : []),
      ...(Array.isArray(optionalSystems.accommodations) ? optionalSystems.accommodations : []),
      ...(Array.isArray(optionalSystems.bridge) ? optionalSystems.bridge : []),
      ...(Array.isArray(optionalSystems.cargo) ? optionalSystems.cargo : []),
      ...(Array.isArray(optionalSystems.drones) ? optionalSystems.drones : []),
      ...(Array.isArray(optionalSystems.sensors) ? optionalSystems.sensors : []),
      ...(Array.isArray(optionalSystems.external) ? optionalSystems.external : []),
      ...(Array.isArray(optionalSystems.internal) ? optionalSystems.internal : []),
      ...(Array.isArray(optionalSystems.commonAreas) ? optionalSystems.commonAreas : []),
    ];

    const system = allSystems.find(s => s.id === systemId);
    if (system) {
      // Update the system with sectionId and sectionName
      (system as any).sectionId = sectionId;
      (system as any).sectionName = sectionName;
    }
  };

  // --- Metal Hydride Storage Cost Calculation ---
  const fuelSystems = installedSystems.filter(isFuelSystemGuard);
  const hasMetalHydride = fuelSystems.some((s) => s.subtype === "metalHydride");
  // Calculate original (pre-halved) tankage
  const fuelTanks = shipState.fuelTanks || 0;
  const collapsibleTanksCapacity = (fuelSystems || [])
    .filter((s) => s.subtype === "collapsibleTank")
    .reduce((sum, s) => sum + (s.properties?.fullTonnage || 0), 0);
  const dropTanksCapacity = (fuelSystems || [])
    .filter((s) => s.subtype === "dropTank")
    .reduce((sum, s) => sum + (s.properties?.dropTankTonnage || 0), 0);
  const fuelCargoContainerCapacity = (fuelSystems || [])
    .filter(
      (s) =>
        s.subtype === "fuelCargoContainer" &&
        s.properties?.allocation === "fuel",
    )
    .reduce((sum, s) => sum + (s.properties?.capacity || 0), 0);
  const originalTotalPhysicalTankage =
    fuelTanks +
    collapsibleTanksCapacity +
    dropTanksCapacity +
    fuelCargoContainerCapacity;
  const metalHydrideCost = hasMetalHydride
    ? originalTotalPhysicalTankage * 200000
    : 0;



  return (
    <div className="w-full pb-32">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Optional Systems
        </h2>
      </div>

      {/* Ship Configuration Warnings */}
      {shipWarnings.length > 0 && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Configuration Required
              </h3>
            </div>
          </div>
          <div className="text-sm text-yellow-700 dark:text-yellow-300">
            <ul className="list-disc list-inside space-y-1">
              {shipWarnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* System Errors */}
      {Object.keys(systemErrors).length > 0 && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                System Loading Errors
              </h3>
            </div>
          </div>
          <div className="text-sm text-red-700 dark:text-red-300">
            <ul className="list-disc list-inside space-y-1">
              {Object.entries(systemErrors).map(([systemType, error]) => (
                <li key={systemType}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-600">
          <div className="flex overflow-x-auto flex-nowrap">
            {systemCategories.map((category, index) => (
              <button
                key={category.value}
                onClick={() => handleTabChange(index)}
                className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  selectedTab === index
                    ? "border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
                id={`optional-systems-tab-${index}`}
                aria-controls={`optional-systems-tabpanel-${index}`}
                role="tab"
                aria-selected={selectedTab === index}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Panels */}
        {systemCategories.map((category, index) => (
          <TabPanel key={category.value} value={selectedTab} index={index}>
            {category.component}
          </TabPanel>
        ))}
      </div>

      {/* Summary section showing selected systems */}
      <div className="mt-6">
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
            Installed Optional Systems Summary
          </h3>
          {installedSystems.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400">No optional systems installed.</div>
          ) : (
            <div className="space-y-6">
              {/* Group by category */}
              {[
                { label: "Structure", guard: isStructureSystemGuard },
                { label: "Modular Hull", guard: isModularHullSystemGuard },
                { label: "Power", guard: isPowerSystemGuard },
                { label: "Drives", guard: isDriveSystemGuard },
                { label: "Fuel", guard: isFuelSystemGuard },
                { label: "Accommodations", guard: isAccommodationSystemGuard },
                { label: "Bridge", guard: isBridgeSystemGuard },
                { label: "Cargo", guard: isCargoSystemGuard },
                { label: "Drones", guard: isDroneSystemGuard },
                { label: "Sensors", guard: isSensorSystemGuard },
                { label: "External", guard: isExternalSystemGuard },
                { label: "Internal", guard: isInternalSystemGuard },
                {
                  label: "Common Areas",
                  guard: (s: OptionalSystem) => s.type === "commonArea",
                },
              ].map(({ label, guard }) => {
                const systems = installedSystems.filter(guard);
                if (systems.length === 0) return null;

                // Special handling for Internal systems to group capsules, stables, and booby trap airlocks
                let displaySystems = systems;
                if (label === "Internal") {
                  displaySystems = groupSystems(systems);

                  // Group booby trap airlocks by configuration (trap type and trapped count)
                  const boobyTrapAirlocks = displaySystems.filter(
                    (s) => s.subtype === "internalBoobyTrap",
                  );
                  const otherInternalSystems = displaySystems.filter(
                    (s) => s.subtype !== "internalBoobyTrap",
                  );

                  if (boobyTrapAirlocks.length > 0) {
                    // Group by configuration (trap type + trapped count)
                    const configGroups = new Map<string, any[]>();

                    boobyTrapAirlocks.forEach((system) => {
                      const trapType =
                        (system.properties as any)?.boobyTrapType || "basic";
                      const trappedCount =
                        (system.properties as any)?.trappedCount || 1;
                      const configKey = `${trapType}-${trappedCount}`;

                      if (!configGroups.has(configKey)) {
                        configGroups.set(configKey, []);
                      }
                      configGroups.get(configKey)!.push(system);
                    });

                    const groupedBoobyTraps = Array.from(
                      configGroups.entries(),
                    ).map(([configKey, systems]) => {
                      const totalCost = (systems || []).reduce(
                        (sum, s) => sum + (s.cost || 0),
                        0,
                      );
                      const totalTonnage = (systems || []).reduce(
                        (sum, s) => sum + (s.tonnage || 0),
                        0,
                      );
                      const totalPower = (systems || []).reduce(
                        (sum, s) => sum + (s.power || 0),
                        0,
                      );

                      return {
                        ...systems[0],
                        id: `boobyTrap-grouped-${configKey}`,
                        cost: totalCost,
                        tonnage: totalTonnage,
                        power: totalPower,
                        _quantity: systems.length,
                        _groupedSystems: systems,
                      } as any;
                    });

                    displaySystems = [
                      ...groupedBoobyTraps,
                      ...otherInternalSystems,
                    ];
                  }

                  // Group medical bays
                  const medicalBays = displaySystems.filter(
                    (s) => s.subtype === "internalMedicalBay",
                  );
                  const otherInternalSystemsAfterBoobyTraps =
                    displaySystems.filter(
                      (s) => s.subtype !== "internalMedicalBay",
                    );

                  if (medicalBays.length > 0) {
                    const totalCost = (medicalBays || []).reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = (medicalBays || []).reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = (medicalBays || []).reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    const groupedMedicalBays = {
                      ...medicalBays[0],
                      id: "medicalBay-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: medicalBays.length,
                      _groupedSystems: medicalBays,
                    } as any;

                    displaySystems = [
                      groupedMedicalBays,
                      ...otherInternalSystemsAfterBoobyTraps,
                    ];
                  }

                  // Group briefing rooms
                  const briefingRooms = displaySystems.filter(
                    (s) => s.subtype === "internalBriefingRoom",
                  );
                  const otherInternalSystemsAfterMedicalBays =
                    displaySystems.filter(
                      (s) => s.subtype !== "internalBriefingRoom",
                    );

                  if (briefingRooms.length > 0) {
                    const totalCost = (briefingRooms || []).reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = (briefingRooms || []).reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = (briefingRooms || []).reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    const groupedBriefingRooms = {
                      ...briefingRooms[0],
                      id: "briefingRoom-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: briefingRooms.length,
                      _groupedSystems: briefingRooms,
                    } as any;

                    displaySystems = [
                      groupedBriefingRooms,
                      ...otherInternalSystemsAfterMedicalBays,
                    ];
                  }
                }

                // Special handling for Bridge systems to group Holographic Controls and Sensor Stations
                if (label === "Bridge") {
                  const holographicControls = systems.filter(
                    (s) => s.subtype === "holographic",
                  );
                  const sensorStations = systems.filter(
                    (s) => s.subtype === "sensorStation",
                  );
                  const otherSystems = systems.filter(
                    (s) =>
                      s.subtype !== "holographic" &&
                      s.subtype !== "sensorStation",
                  );

                  const groupedSystems = [];

                  // Group Holographic Controls
                  if (holographicControls.length > 0) {
                    const totalCost = (holographicControls || []).reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = (holographicControls || []).reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = (holographicControls || []).reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    groupedSystems.push({
                      ...holographicControls[0],
                      id: "holographic-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: holographicControls.length,
                      _groupedSystems: holographicControls,
                    } as any);
                  }

                  // Group Sensor Stations
                  if (sensorStations.length > 0) {
                    const totalCost = (sensorStations || []).reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = (sensorStations || []).reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = (sensorStations || []).reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    groupedSystems.push({
                      ...sensorStations[0],
                      id: "sensorStation-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: sensorStations.length,
                      _groupedSystems: sensorStations,
                    } as any);
                  }

                  displaySystems = [...groupedSystems, ...otherSystems];
                }

                // Special handling for Cargo systems to group Loading Belts and Cargo Cranes
                if (label === "Cargo") {
                  const loadingBelt7s = systems.filter(
                    (s) => s.subtype === "loadingBelt7",
                  );
                  const loadingBelt12s = systems.filter(
                    (s) => s.subtype === "loadingBelt12",
                  );
                  const cargoCranes = systems.filter(
                    (s) => s.subtype === "cargoCrane",
                  );
                  const otherSystems = systems.filter(
                    (s) =>
                      s.subtype !== "loadingBelt7" &&
                      s.subtype !== "loadingBelt12" &&
                      s.subtype !== "cargoCrane",
                  );

                  const groupedSystems = [];

                  // Group Cargo Cranes
                  if (cargoCranes.length > 0) {
                    const totalCost = (cargoCranes || []).reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = (cargoCranes || []).reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = (cargoCranes || []).reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    const totalCargoSpace = (cargoCranes || []).reduce((sum, s) => {
                      const cargoSystem = s as CargoSystem;
                      return sum + (cargoSystem.properties?.cargoSpace || 0);
                    }, 0);
                    groupedSystems.push({
                      ...cargoCranes[0],
                      id: "cargoCrane-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: cargoCranes.length,
                      _groupedSystems: cargoCranes,
                      _totalCargoSpace: totalCargoSpace,
                    } as any);
                  }

                  // Group Loading Belt TL7
                  if (loadingBelt7s.length > 0) {
                    const totalCost = loadingBelt7s.reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = loadingBelt7s.reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = loadingBelt7s.reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    const totalCrewReplacement = loadingBelt7s.length * 10; // 10 crew per TL7 belt
                    groupedSystems.push({
                      ...loadingBelt7s[0],
                      id: "loadingBelt7-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: loadingBelt7s.length,
                      _groupedSystems: loadingBelt7s,
                      _totalCrewReplacement: totalCrewReplacement,
                    } as any);
                  }

                  // Group Loading Belt TL12
                  if (loadingBelt12s.length > 0) {
                    const totalCost = loadingBelt12s.reduce(
                      (sum, s) => sum + (s.cost || 0),
                      0,
                    );
                    const totalTonnage = loadingBelt12s.reduce(
                      (sum, s) => sum + (s.tonnage || 0),
                      0,
                    );
                    const totalPower = loadingBelt12s.reduce(
                      (sum, s) => sum + (s.power || 0),
                      0,
                    );
                    const totalCrewReplacement = loadingBelt12s.length * 25; // 25 crew per TL12 belt
                    groupedSystems.push({
                      ...loadingBelt12s[0],
                      id: "loadingBelt12-grouped",
                      cost: totalCost,
                      tonnage: totalTonnage,
                      power: totalPower,
                      _quantity: loadingBelt12s.length,
                      _groupedSystems: loadingBelt12s,
                      _totalCrewReplacement: totalCrewReplacement,
                    } as any);
                  }

                  displaySystems = [...groupedSystems, ...otherSystems];
                }

                // Special handling for Drones to group all drone types
                if (label === "Drones") {
                  // Define drone subtypes that should be grouped
                  const DRONE_TYPES = [
                    "mining",
                    "probe",
                    "advancedProbe",
                    "repair",
                  ];

                  const groupedSystems: any[] = [];
                  const otherSystems: any[] = [];

                  // Group drones by subtype
                  DRONE_TYPES.forEach((subtype) => {
                    const dronesOfType = systems.filter(
                      (s) => s.subtype === subtype,
                    );

                    if (dronesOfType.length === 0) {
                      return; // No drones of this type
                    }

                    if (dronesOfType.length === 1) {
                      // Single drone, don't group
                      otherSystems.push(dronesOfType[0]);
                    } else {
                      // Multiple drones, group them
                      const totalCost = dronesOfType.reduce(
                        (sum, s) => sum + (s.cost || 0),
                        0,
                      );
                      const totalTonnage = dronesOfType.reduce(
                        (sum, s) => sum + (s.tonnage || 0),
                        0,
                      );
                      const totalPower = dronesOfType.reduce(
                        (sum, s) => sum + (s.power || 0),
                        0,
                      );

                      groupedSystems.push({
                        ...dronesOfType[0],
                        id: `${subtype}Drone-grouped`,
                        cost: totalCost,
                        tonnage: totalTonnage,
                        power: totalPower,
                        _quantity: dronesOfType.length,
                        _groupedSystems: dronesOfType,
                      } as any);
                    }
                  });

                  displaySystems = [...groupedSystems, ...otherSystems];
                }

                return (
                  <div key={label}>
                    <h4 className="text-md font-semibold mb-2 text-blue-700 dark:text-blue-400">
                      {label}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 dark:border-gray-600 rounded">
                        <thead>
                          <tr className="bg-gray-100 dark:bg-gray-700">
                            <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                              Name
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                              Tonnage
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                              Cost
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                              Details
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                              Actions
                            </th>
                            {breakawayHull?.isBreakaway && (
                              <th className="px-2 py-1 text-left font-medium text-gray-900 dark:text-gray-100">
                                Assign to Section?
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {label === "Fuel" && hasMetalHydride && (
                            <tr className="bg-white dark:bg-gray-800">
                              <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                Metal Hydride Storage
                              </td>
                              <td className="px-2 py-1 text-gray-900 dark:text-gray-100">See Step 4</td>
                              <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                {formatCurrency(metalHydrideCost)}
                              </td>
                              <td className="px-2 py-1 text-gray-900 dark:text-gray-100"></td>
                              <td className="px-2 py-1">
                                <button
                                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs mr-2"
                                  onClick={() =>
                                    handleRemoveSystem(
                                      fuelSystems.find(
                                        (s) => s.subtype === "metalHydride",
                                      ),
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </td>
                              {breakawayHull?.isBreakaway && (
                                <td className="px-2 py-1">
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={systemSectionAssignments['metalHydride'] !== undefined}
                                      onChange={(e) => handleSectionAssignmentToggle('metalHydride', e.target.checked)}
                                      className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                    />
                                    {systemSectionAssignments['metalHydride'] && (
                                      <select
                                        value={systemSectionAssignments['metalHydride']}
                                        onChange={(e) => handleSectionChange('metalHydride', e.target.value)}
                                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                      >
                                        {breakawayHull.sections.map(section => (
                                          <option key={section.id} value={section.id}>
                                            {section.name}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          )}
                          {displaySystems
                            .filter((s) => s.subtype !== "metalHydride")
                            .map((system) => (
                              <React.Fragment key={system.id}>
                                <tr className="border-t border-gray-100 dark:border-gray-600 bg-white dark:bg-gray-800">
                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">{system.name}</td>
                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                    {typeof system.tonnage === "number"
                                      ? system.tonnage.toLocaleString(
                                          undefined,
                                          { maximumFractionDigits: 2 },
                                        )
                                      : system.tonnage}
                                  </td>
                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                    {formatCurrency(system.cost || 0)}
                                  </td>
                                  <td className="px-2 py-1 text-gray-600 dark:text-gray-300">
                                    {(() => {
                                      if (
                                        system.subtype === "gourmetKitchen" &&
                                        system.properties.diners
                                      ) {
                                        return `${system.properties.diners} diners`;
                                      }
                                      if (
                                        system.subtype === "hotTub" &&
                                        system.properties.travellers
                                      ) {
                                        return `${system.properties.travellers} travellers`;
                                      }
                                      if (
                                        system.subtype === "swimmingPool" ||
                                        system.subtype === "endlessPool"
                                      ) {
                                        const poolType =
                                          system.properties.poolType || "";
                                        const travellers = system.properties
                                          .travellers
                                          ? `${system.properties.travellers} travellers`
                                          : "";
                                        return `${poolType} ${travellers}`.trim();
                                      }
                                      if (system.subtype === "zeroGRoom") {
                                        return `${system.tonnage} tons`;
                                      }
                                      if (
                                        system.subtype ===
                                        "advancedEntertainment"
                                      ) {
                                        return `TL${system.techLevel}`;
                                      }
                                      // NEW: Laboratory, Studio, Training Facilities details
                                      if (
                                        system.subtype ===
                                          "internalLaboratory" &&
                                        system.properties.scientists
                                      ) {
                                        return `Suitable for up to ${system.properties.scientists} scientists`;
                                      }
                                      if (
                                        system.subtype === "internalStudio" &&
                                        system.properties.people
                                      ) {
                                        return `Suitable for up to ${system.properties.people} users`;
                                      }
                                      if (
                                        system.subtype ===
                                          "internalTrainingFacilities" &&
                                        system.properties.people
                                      ) {
                                        return `Suitable for up to ${system.properties.people} trainees`;
                                      }
                                      // NEW: Stateroom quantity details
                                      if (
                                        (system.subtype === "highStateroom" ||
                                          system.subtype ===
                                            "luxuryStateroom") &&
                                        system.properties.quantity
                                      ) {
                                        return `Quantity: ${system.properties.quantity}`;
                                      }
                                      // NEW: Acceleration Seating passenger details
                                      if (
                                        (system.subtype ===
                                          "accelerationBench" ||
                                          system.subtype ===
                                            "accelerationSeat") &&
                                        system.properties.seats
                                      ) {
                                        const seats =
                                          Number(system.properties.seats) || 0;
                                        const quantity =
                                          Number(system.properties.quantity) ||
                                          1;
                                        const totalSeats = seats * quantity;
                                        return `Seats ${totalSeats} passengers`;
                                      }
                                      // NEW: Barracks accommodation details
                                      if (
                                        system.subtype === "barracks" &&
                                        system.properties.quantity
                                      ) {
                                        const people =
                                          Number(system.properties.quantity) ||
                                          1;
                                        const designation =
                                          system.properties.designation ||
                                          "passenger";
                                        const designationText =
                                          designation === "crew"
                                            ? "Crew"
                                            : "Basic Passage";
                                        return `Accommodation for ${people} ${designationText}`;
                                      }
                                      // NEW: Cabin Space passenger details
                                      if (
                                        system.subtype === "cabinSpace" &&
                                        system.properties.quantity
                                      ) {
                                        const passengers =
                                          Number(system.properties.quantity) ||
                                          1;
                                        return `Suitable for ${passengers} passengers`;
                                      }
                                      // NEW: Brig prisoner capacity details
                                      if (
                                        system.subtype === "brig" &&
                                        system.properties
                                      ) {
                                        const comfortablePrisoners =
                                          Number(system.properties.prisoners) ||
                                          6;
                                        const maxPrisoners =
                                          Number(
                                            system.properties.maxPrisoners,
                                          ) || 12;
                                        const quantity =
                                          Number(system.properties.quantity) ||
                                          1;
                                        const totalComfortable =
                                          comfortablePrisoners * quantity;
                                        const totalMax =
                                          maxPrisoners * quantity;
                                        return `Suitable for ${totalComfortable} prisoners (up to ${totalMax} uncomfortably)`;
                                      }
                                      // NEW: Multi-Environment Space details
                                      if (
                                        system.subtype ===
                                          "multiEnvironmentSpace" &&
                                        (system as AccommodationSystem)
                                          .coveredSpaces
                                      ) {
                                        const coveredSpaces = (
                                          system as AccommodationSystem
                                        ).coveredSpaces!;
                                        const customArea = coveredSpaces.find(
                                          (space: {
                                            id: string;
                                            label: string;
                                            tons: number;
                                            environmentType: string;
                                          }) => space.id === "custom",
                                        );
                                        if (customArea) {
                                          return `${customArea.tons} tons, ${customArea.environmentType}`;
                                        }
                                        const areas = coveredSpaces.map(
                                          (space: {
                                            id: string;
                                            label: string;
                                            tons: number;
                                            environmentType: string;
                                          }) =>
                                            `${space.label} (${space.tons} tons)`,
                                        );
                                        return areas.join(", ");
                                      }
                                      // NEW: Holographic Controls and Sensor Station quantity details
                                      if (
                                        (system.subtype === "holographic" ||
                                          system.subtype === "sensorStation") &&
                                        (system as any)._quantity
                                      ) {
                                        return `Number Installed: ${(system as any)._quantity}`;
                                      }
                                      // NEW: External Cargo Mount capacity details
                                      if (
                                        system.subtype ===
                                          "externalCargoMount" &&
                                        system.properties?.externalCargoCapacity
                                      ) {
                                        const capacity = Number(
                                          system.properties
                                            .externalCargoCapacity,
                                        );
                                        return `Capacity: ${capacity.toLocaleString()} tons`;
                                      }
                                      // NEW: Cargo Crane capacity details
                                      if (system.subtype === "cargoCrane") {
                                        // For grouped Cargo Cranes, show total cargo space
                                        if (
                                          (system as any)._quantity &&
                                          (system as any)._totalCargoSpace
                                        ) {
                                          return `Capacity: ${(system as any)._totalCargoSpace.toLocaleString()} tons (${(system as any)._quantity} installed)`;
                                        }
                                        // For individual Cargo Cranes (fallback)
                                        const cargoSpace =
                                          system.properties?.cargoSpace || 0;
                                        return `Capacity: ${cargoSpace.toLocaleString()} tons`;
                                      }
                                      // NEW: Jump Net type and capacity details
                                      if (
                                        (system.subtype ===
                                          "jumpNetInterplanetary" ||
                                          system.subtype ===
                                            "jumpNetInterstellar") &&
                                        system.properties?.jumpNetCargoCapacity
                                      ) {
                                        const capacity = Number(
                                          system.properties
                                            .jumpNetCargoCapacity,
                                        );
                                        const type =
                                          system.subtype ===
                                          "jumpNetInterplanetary"
                                            ? "Interplanetary"
                                            : "Interstellar";
                                        return `${type}, Capacity: ${capacity.toLocaleString()} tons`;
                                      }
                                      // NEW: Loading Belt type details
                                      if (
                                        system.subtype === "loadingBelt7" ||
                                        system.subtype === "loadingBelt12"
                                      ) {
                                        // For grouped Loading Belts, show total crew replacement
                                        if (
                                          (system as any)._quantity &&
                                          (system as any)._totalCrewReplacement
                                        ) {
                                          return `Replaces ${(system as any)._totalCrewReplacement} crewmen (${(system as any)._quantity} installed)`;
                                        }
                                        // For individual Loading Belts (fallback)
                                        const crewReplacement =
                                          system.subtype === "loadingBelt7"
                                            ? 10
                                            : 25;
                                        return `Replaces ${crewReplacement} crewmen`;
                                      }
                                      // NEW: Drone quantity details
                                      if (system.type === "drone") {
                                        // For grouped drones, calculate total quantity
                                        if ((system as any)._quantity) {
                                          const individualQuantity =
                                            system.properties?.quantity || 1;
                                          const totalQuantity =
                                            individualQuantity *
                                            (system as any)._quantity;
                                          return `Quantity: ${totalQuantity}`;
                                        }
                                        // For individual drones, show quantity from properties
                                        const quantity =
                                          system.properties?.quantity || 0;
                                        if (quantity > 0) {
                                          return `Quantity: ${quantity}`;
                                        }
                                        // For Repair Drones without quantity (fallback)
                                        if (system.subtype === "repair") {
                                          return "Quantity: 1";
                                        }
                                      }
                                      // NEW: Deep Penetration Scanner scan rate details
                                      if (
                                        system.subtype ===
                                          "deepPenetrationScanners" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const scanRate = system.tonnage * 20; // 20 tons/hour per 1 ton of scanners
                                        return `Scans ${scanRate} Tons/hour`;
                                      }
                                      // NEW: Docking Clamp type details
                                      if (
                                        system.subtype === "dockingClamp" &&
                                        system.properties?.clampType
                                      ) {
                                        const clampType = system.properties
                                          .clampType as string;
                                        const typeMap: {
                                          [key: string]: string;
                                        } = {
                                          typeI: "Type I",
                                          typeII: "Type II",
                                          typeIII: "Type III",
                                          typeIV: "Type IV",
                                          typeV: "Type V",
                                        };
                                        const typeLabel =
                                          typeMap[clampType] || "Unknown Type";
                                        const maxTonnage =
                                          system.properties
                                            .maxAttachedTonnage || 0;
                                        return `${typeLabel} (up to ${maxTonnage} tons)`;
                                      }
                                      // NEW: Forced Linkage Apparatus type details
                                      if (
                                        system.subtype === "forcedLinkage" &&
                                        system.properties?.linkageType
                                      ) {
                                        const linkageType = system.properties
                                          .linkageType as string;
                                        const typeMap: {
                                          [key: string]: string;
                                        } = {
                                          basic: "Basic (TL7)",
                                          improved: "Improved (TL9)",
                                          enhanced: "Enhanced (TL12)",
                                          advanced: "Advanced (TL15)",
                                        };
                                        const typeLabel =
                                          typeMap[linkageType] ||
                                          "Unknown Type";
                                        const pilotDM =
                                          system.properties.pilotDM || 0;
                                        const dmText =
                                          pilotDM >= 0
                                            ? `+${pilotDM}`
                                            : `${pilotDM}`;
                                        return `${typeLabel}, Pilot DM ${dmText}`;
                                      }
                                      // NEW: Grappling Arm type details
                                      if (
                                        system.subtype === "grapplingArm" &&
                                        system.properties?.armType
                                      ) {
                                        const armType = system.properties
                                          .armType as string;
                                        const typeMap: {
                                          [key: string]: string;
                                        } = {
                                          standard: "Standard",
                                          heavy: "Heavy",
                                        };
                                        const typeLabel =
                                          typeMap[armType] || "Unknown Type";
                                        const maxWeight =
                                          system.properties.maxWeight || 0;
                                        return `${typeLabel} (up to ${maxWeight} tons)`;
                                      }
                                      // NEW: Booby Trap Airlock details
                                      if (
                                        system.subtype ===
                                          "internalBoobyTrap" &&
                                        system.properties?.boobyTrapType
                                      ) {
                                        // For grouped booby trap airlocks, show total details
                                        if (
                                          (system as any)._quantity &&
                                          (system as any)._groupedSystems
                                        ) {
                                          const groupedSystems = (system as any)
                                            ._groupedSystems;
                                          // Since all systems in a group have the same configuration, we can use the first one
                                          const firstSystem = groupedSystems[0];
                                          const trappedCount =
                                            firstSystem.properties
                                              ?.trappedCount || 1;
                                          const totalTrappedCount =
                                            trappedCount *
                                            (system as any)._quantity;
                                          const trapType = system.properties
                                            .boobyTrapType as string;
                                          const typeMap: {
                                            [key: string]: string;
                                          } = {
                                            basic: "Basic",
                                            improved: "Improved",
                                            enhanced: "Enhanced",
                                            advanced: "Advanced",
                                          };
                                          const typeLabel =
                                            typeMap[trapType] || "Unknown Type";
                                          return `${typeLabel} Trap, ${totalTrappedCount} airlock${totalTrappedCount > 1 ? "s" : ""} trapped`;
                                        } else {
                                          // For individual booby trap airlocks
                                          const trapType = system.properties
                                            .boobyTrapType as string;
                                          const typeMap: {
                                            [key: string]: string;
                                          } = {
                                            basic: "Basic",
                                            improved: "Improved",
                                            enhanced: "Enhanced",
                                            advanced: "Advanced",
                                          };
                                          const typeLabel =
                                            typeMap[trapType] || "Unknown Type";
                                          const trappedCount =
                                            system.properties.trappedCount || 1;
                                          return `${typeLabel} Trap, ${trappedCount} airlock${trappedCount > 1 ? "s" : ""} trapped`;
                                        }
                                      }
                                      // NEW: UNREP system transfer rate details
                                      if (
                                        system.subtype ===
                                          "internalUnrepSystem" &&
                                        typeof system.tonnage === "number" &&
                                        typeof system.properties
                                          ?.transferPerTon === "number"
                                      ) {
                                        const transferRate =
                                          system.tonnage *
                                          system.properties.transferPerTon;
                                        return `Capable of Transfers at ${transferRate} tons/hour`;
                                      }
                                      // NEW: Vault armor and hits details
                                      if (
                                        system.subtype === "internalVault" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const armor = Math.min(
                                          system.tonnage,
                                          10,
                                        ); // 1 per ton, max 10
                                        const hits = Math.ceil(
                                          system.tonnage / 5,
                                        ); // 1 per 5 tons
                                        return `Armor: ${armor}, Hits: ${hits}`;
                                      }
                                      // NEW: Capsule type and quantity details
                                      if (
                                        [
                                          "internalReEntryCapsule",
                                          "internalAssaultCapsule",
                                          "internalHighSurvivabilityCapsule",
                                          "internalReEntryPod",
                                        ].includes(system.subtype)
                                      ) {
                                        // For grouped capsules, use the count from grouped systems
                                        if (isGroupedSystem(system)) {
                                          const capsuleCount =
                                            system._groupedSystems.length;
                                          const capsuleType = system.name;
                                          return `${capsuleType} (${capsuleCount})`;
                                        } else {
                                          // For individual capsules (fallback)
                                          const capsuleCount = systems.filter(
                                            (s) => s.subtype === system.subtype,
                                          ).length;
                                          const capsuleType = system.name;
                                          return `${capsuleType} (${capsuleCount})`;
                                        }
                                      }
                                      // NEW: Armoury crew/marine capacity details
                                      if (
                                        system.subtype === "internalArmoury" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const crewCapacity =
                                          system.tonnage * 25; // 25 crew per ton
                                        const marineCapacity =
                                          system.tonnage * 5; // 5 marines per ton
                                        return `Serves ${crewCapacity} crew or ${marineCapacity} marines`;
                                      }
                                      // NEW: Biosphere passenger capacity details
                                      if (
                                        system.subtype ===
                                          "internalBiosphere" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const passengerCapacity =
                                          system.tonnage * 2; // 2 passengers per ton
                                        return `Eliminates life support cost for ${passengerCapacity} passengers (already incorporated)`;
                                      }
                                      // NEW: Medical Bay patient capacity details
                                      if (
                                        system.subtype === "internalMedicalBay"
                                      ) {
                                        // For grouped medical bays, show total patient capacity
                                        if (
                                          (system as any)._quantity &&
                                          (system as any)._groupedSystems
                                        ) {
                                          const totalPatients =
                                            (system as any)._quantity * 3; // 3 patients per medical bay
                                          return `Treats ${totalPatients} patients`;
                                        } else {
                                          // For individual medical bays
                                          return `Treats 3 patients`;
                                        }
                                      }

                                      // NEW: Concealed Compartment contraband capacity details
                                      if (
                                        system.subtype ===
                                          "internalConcealedCompartment" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        return `Contraband Capacity: ${system.tonnage} tons`;
                                      }

                                      // NEW: Construction Deck build capacity details
                                      if (
                                        system.subtype ===
                                          "internalConstructionDeck" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Fix floating-point precision issue: use integer math to avoid calculation errors
                                        const buildCapacity = Math.floor(
                                          (system.tonnage * 5) / 10,
                                        ); // Can build up to half the deck's tonnage
                                        return `For builds up to ${buildCapacity} tons`;
                                      }

                                      // NEW: Docking Space maximum vessel size details
                                      if (
                                        system.subtype ===
                                          "internalDockingSpace" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Fix floating-point precision issue: use integer math to avoid division errors
                                        const maxVesselSize = Math.floor(
                                          (system.tonnage * 10) / 11,
                                        );
                                        return `Maximum size of contained vessel/vehicle: ${maxVesselSize} tons`;
                                      }

                                      // NEW: Full Hangar craft capacity details
                                      if (
                                        system.subtype ===
                                          "internalFullHangar" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Full hangar tonnage = 2 × craft tonnage, so craft capacity = tonnage / 2
                                        const craftCapacity = Math.floor(
                                          system.tonnage / 2,
                                        );
                                        return `Total Craft Capacity: ${craftCapacity} tons`;
                                      }

                                      // NEW: Grav Screen auto-calculated details
                                      if (
                                        system.subtype ===
                                          "internalGravScreen" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Grav screen tonnage = 1 ton per 200 tons of hull
                                        return `Auto-calculated for ${shipSize} ton hull (1 ton per 200 tons)`;
                                      }

                                      // NEW: Launch Tube size maximum details
                                      if (
                                        system.subtype ===
                                          "internalLaunchTube" &&
                                        system.properties?.launchTubeMaxSize
                                      ) {
                                        return `Launch Tube size maximum (tons): ${system.properties.launchTubeMaxSize}`;
                                      }

                                      // NEW: Recovery Deck size maximum details
                                      if (
                                        system.subtype ===
                                          "internalRecoveryDeck" &&
                                        system.properties?.recoveryDeckMaxSize
                                      ) {
                                        return `Recovery Deck size maximum (tons): ${system.properties.recoveryDeckMaxSize}`;
                                      }

                                      // NEW: Stable creature capacity details
                                      if (
                                        system.subtype === "internalStable" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // For grouped stables, show total capacity and quantity
                                        if (isGroupedSystem(system)) {
                                          const quantity =
                                            system._groupedSystems.length;
                                          const humanCapacity = Math.floor(
                                            (system.tonnage / 10) * 20,
                                          );
                                          const cattleCapacity = Math.floor(
                                            (system.tonnage / 10) * 10,
                                          );
                                          return `Suitable for ${cattleCapacity} cattle-size creatures or ${humanCapacity} human-size creatures (${quantity} installed)`;
                                        } else {
                                          // For individual stables
                                          const humanCapacity = Math.floor(
                                            (system.tonnage / 10) * 20,
                                          );
                                          const cattleCapacity = Math.floor(
                                            (system.tonnage / 10) * 10,
                                          );
                                          return `Suitable for ${cattleCapacity} cattle-size creatures or ${humanCapacity} human-size creatures`;
                                        }
                                      }

                                      // NEW: Workshop mechanics capacity details
                                      if (
                                        system.subtype === "internalWorkshop" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // For grouped workshops, show total capacity and quantity
                                        if (isGroupedSystem(system)) {
                                          const quantity =
                                            system._groupedSystems.length;
                                          const mechanicsCapacity = Math.floor(
                                            system.tonnage / 3,
                                          ); // 3 tons per mechanic (6 tons / 2 mechanics)
                                          return `Suitable for ${mechanicsCapacity} mechanics (${quantity} installed)`;
                                        } else {
                                          // For individual workshops
                                          const mechanicsCapacity = Math.floor(
                                            system.tonnage / 3,
                                          ); // 3 tons per mechanic (6 tons / 2 mechanics)
                                          return `Suitable for ${mechanicsCapacity} mechanics`;
                                        }
                                      }
                                      // NEW: Solar Panels deployment power details
                                      if (
                                        system.subtype === "solarPanels" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Get the correct power per unit based on system properties and tech level
                                        const powerPerUnit =
                                          system.properties?.powerPerUnit?.[
                                            system.techLevel
                                          ] ||
                                          system.properties
                                            ?.powerPerUnit?.[12] ||
                                          0.25;
                                        const deploymentPower =
                                          system.tonnage * powerPerUnit;
                                        return `Generates ${deploymentPower.toFixed(1)} Power when deployed`;
                                      }
                                      // NEW: High-Efficiency Batteries storage capacity details
                                      if (
                                        system.subtype === "battery" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const storageCapacity =
                                          system.power || 0;
                                        return `Storage Capacity: ${storageCapacity.toFixed(1)} Power`;
                                      }
                                      // NEW: High-Burn Thruster thrust rating details
                                      if (
                                        system.subtype === "highBurn" &&
                                        system.properties?.selectedThrust
                                      ) {
                                        return `Thrust Rating: ${system.properties.selectedThrust}`;
                                      }
                                      // NEW: Concealed Manoeuvre Drive thrust reduction details
                                      if (system.subtype === "concealed") {
                                        const maneuverDrive = drives.find(
                                          (d) => d.type === "maneuver",
                                        );
                                        if (maneuverDrive) {
                                          const reducedThrust = Math.floor(
                                            maneuverDrive.rating * 0.5,
                                          );
                                          return `Reduces M-Drive Thrust to ${reducedThrust}`;
                                        }
                                        return "Reduces M-Drive Thrust by 50%";
                                      }
                                      // Armoured Bulkhead protected areas details
                                      if (
                                        system.subtype === "armouredBulkhead" &&
                                        system.type === "structure"
                                      ) {
                                        const structureSystem =
                                          system as StructureSystem;
                                        if (
                                          Array.isArray(
                                            structureSystem.protectedSystems,
                                          ) &&
                                          structureSystem.protectedSystems
                                            .length > 0
                                        ) {
                                          const formatSystemName = (
                                            systemId: string,
                                          ): string => {
                                            const nameMap: Record<
                                              string,
                                              string
                                            > = {
                                              bridge: "Bridge",
                                              computer: "Computer",
                                              sensors: "Sensors",
                                              powerPlant: "Power Plant",
                                              maneuverDrive: "Manoeuvre Drive",
                                              jumpDrive: "Jump Drive",
                                              reactionDrive: "Reaction Drive",
                                              fuelTanks: "Fuel Tanks",
                                              staterooms: "Staterooms",
                                              cargo: "Cargo",
                                              weapons: "Weapons",
                                              hangar: "Hangar",
                                              medicalBay: "Medical Bay",
                                              droneBay: "Drone Bay",
                                              externalSystems:
                                                "External Systems",
                                              internalSystems:
                                                "Internal Systems",
                                            };
                                            return (
                                              nameMap[systemId] || systemId
                                            );
                                          };
                                          const formattedNames =
                                            structureSystem.protectedSystems.map(
                                              formatSystemName,
                                            );
                                          return `Protects: ${formattedNames.join(", ")}`;
                                        }
                                      }
                                      // NEW: Collapsible Fuel Tank details
                                      if (
                                        system.subtype === "collapsibleTank" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        return `Contains up to ${system.tonnage} Tons`;
                                      }
                                      // NEW: Drop Tank details
                                      if (
                                        system.subtype === "dropTank" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        return `Contains up to ${system.tonnage} Tons`;
                                      }
                                      // NEW: Fuel/Cargo Container details
                                      if (
                                        system.subtype === "fuelCargoContainer"
                                      ) {
                                        const config =
                                          (system.properties
                                            ?.allocation as string) || "fuel";
                                        const capacity =
                                          system.properties?.capacity || 0;
                                        return `Contains up to ${capacity} Tons, currently configured for ${config.charAt(0).toUpperCase() + config.slice(1)}`;
                                      }
                                      // NEW: Fuel Processor details
                                      if (
                                        system.subtype === "fuelProcessor" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        const processingRate =
                                          system.tonnage * 20; // 20 tons per day per ton of processor
                                        return `Processes ${processingRate.toFixed(1)} Tons of Fuel per day`;
                                      }
                                      // NEW: Fuel Tank Compartment details
                                      if (
                                        system.subtype === "fuelTankCompartment"
                                      ) {
                                        // Cargo capacity equals the tonnage of the compartment
                                        const cargoCapacity = system.tonnage || 0;
                                        return `Contains up to ${cargoCapacity.toFixed(1)} Tons of Cargo`;
                                      }
                                      // NEW: Mountable Tank details
                                      if (
                                        system.subtype === "mountableTank" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        return `Contains up to ${system.tonnage} Tons`;
                                      }
                                      // NEW: Ramscoop details
                                      if (
                                        system.subtype === "ramscoop" &&
                                        typeof system.tonnage === "number"
                                      ) {
                                        // Use the collectionRate from system properties if available, otherwise calculate it
                                        const collectionRate =
                                          (system.properties as any)
                                            ?.collectionRate ||
                                          system.tonnage * 5;
                                        return `Collects ${collectionRate} Tons of fuel per week`;
                                      }
                                      return "";
                                    })()}
                                  </td>
                                  <td className="px-2 py-1">
                                    <button
                                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs mr-2"
                                      onClick={() => {
                                        if (isGroupedSystem(system)) {
                                          removeGroupedSystem(
                                            system,
                                            handleRemoveSystem,
                                          );
                                        } else if (
                                          (system as any)._groupedSystems &&
                                          (system as any)._groupedSystems
                                            .length > 0
                                        ) {
                                          // Handle grouped Holographic Controls, Sensor Stations, Repair Drones, Booby Trap Airlocks, Medical Bays, and Briefing Rooms
                                          (
                                            system as any
                                          )._groupedSystems.forEach(
                                            (groupedSystem: OptionalSystem) => {
                                              handleRemoveSystem(groupedSystem);
                                            },
                                          );
                                        } else {
                                          handleRemoveSystem(system);
                                        }
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                  {breakawayHull?.isBreakaway && (
                                    <td className="px-2 py-1">
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={systemSectionAssignments[system.id] !== undefined}
                                          onChange={(e) => handleSectionAssignmentToggle(system.id, e.target.checked)}
                                          className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                                        />
                                        {systemSectionAssignments[system.id] && (
                                          <select
                                            value={systemSectionAssignments[system.id]}
                                            onChange={(e) => handleSectionChange(system.id, e.target.value)}
                                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                          >
                                            {breakawayHull.sections.map(section => (
                                              <option key={section.id} value={section.id}>
                                                {section.name}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                {/* Always show modules table for Modular Hull if modules exist */}
                                {label === "Modular Hull" &&
                                  "modules" in system &&
                                  Array.isArray(system.modules) &&
                                  system.modules.length > 0 && (
                                    <tr>
                                      <td
                                        colSpan={breakawayHull?.isBreakaway ? 6 : 5}
                                        className="bg-gray-50 dark:bg-gray-700 p-2"
                                      >
                                        <div className="font-semibold mb-1 text-gray-900 dark:text-gray-100">
                                          Modules
                                        </div>
                                        <table className="min-w-full text-xs border border-gray-200 dark:border-gray-600 rounded">
                                          <thead>
                                            <tr className="bg-gray-100 dark:bg-gray-600">
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Name
                                              </th>
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Tonnage
                                              </th>
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Fit-out Weight
                                              </th>
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Fit-out Cost
                                              </th>
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Installed
                                              </th>
                                              <th className="px-2 py-1 text-left text-gray-900 dark:text-gray-100">
                                                Description
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(system as any).modules.map(
                                              (
                                                mod: import("../../../types/optionalSystems").ModuleItem,
                                                idx: number,
                                              ) => (
                                                <tr key={idx} className="bg-white dark:bg-gray-700">
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {mod.name}
                                                  </td>
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {mod.tonnage}
                                                  </td>
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {mod.fitoutWeight || 0}
                                                  </td>
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {formatCurrency(
                                                      mod.fitoutCost || 0,
                                                    )}
                                                  </td>
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {mod.installed
                                                      ? "Yes"
                                                      : "No"}
                                                  </td>
                                                  <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                                    {mod.description || ""}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                              </React.Fragment>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Sticky footer for Optional Systems totals */}
      {(
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto flex flex-row items-center justify-between">
            <div className="flex flex-row gap-8">
              <div>
                <span className="text-gray-400 block text-xs">
                  Total Optional Systems Cost
                </span>
                <span className="text-xl font-bold text-green-400">
                  {formatCurrency(totalCost + metalHydrideCost)}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs">
                  Total Optional Systems Tonnage
                </span>
                <span className="text-xl font-bold text-blue-400">
                  {totalTonnage.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}{" "}
                  tons
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs">
                  Total Optional Systems Power
                </span>
                <span className="text-xl font-bold text-yellow-400">
                  {totalPower.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}{" "}
                  Power
                </span>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default OptionalSystems;
